<script lang="ts" setup>
import GoalForm from '@/components/goals/GoalForm.vue';
import { Mutations, ShiftCMSState } from '@/store';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import { GoalsActions, GoalsState } from '@/stores/goals.store';
import { Goal } from '@/models/goal.model';
import { useEvents, Event } from '@/services/events.composable';
import TranslatedState from '../common/TranslatedState.vue';
import ListFilter from '../common/ListFilter.vue';
import { FilterData } from '@/services/users.service.js';

const store = useStore<ShiftCMSState & { go: GoalsState }>();
const total = computed<number>(() => store.state.go.goalsTotal);
const items = computed<Goal[]>(() => store.state.go.items);
const events = useEvents();

const filterData = ref<FilterData>({
  search: '',
  level: null,
  page: 1,
  count: 25,
  sort: 'id',
  direction: 'asc',
});

const update = () => {
  store.dispatch(GoalsActions.GetGoals);
};

onMounted(() => {
  update();
  events.on(Event.LanguageChanged, update);
});

onBeforeUnmount(() => {
  store.commit(Mutations.HideDrawer);
  events.off(Event.LanguageChanged, update);
});

const editGoal = (_: Event, { item }: { item: Goal }) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(GoalForm),
    data: item,
  });
};

const addGoal = () => {
  store.commit(Mutations.ShowDrawer, { component: markRaw(GoalForm) });
};

const headers = [
  { title: 'ID', key: 'id', sortable: false, width: '5%' },
  { title: 'Title', key: 'title', width: '40%' },
  { title: 'Main', key: 'mainCategory.title', width: '15%' },
  { title: 'Sub', key: 'subCategory.title', width: '15%' },
  { title: 'Date', key: 'creationDate', width: '20%' },
  { title: 'State', key: 'translated', width: '10%' },
];

const filter = ref<string>('');
const filterTitle = ref<string>('');
const filteredItems = computed<Goal[]>(() => {
  return items.value.filter((v: Goal) => {
    if (filterTitle.value) {
      return [v.title, v.description]
        .map((s) => s.toLowerCase())
        .join('')
        .includes(filterTitle.value);
    }

    return true;
  });
});

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filter.value = filters.filter;
  filterTitle.value = filters.filterTitle;

  console.log(filters.filter, filters.filterTitle);
}

function updateItemsPerPage(count: number) {
  filterData.value = { ...filterData.value, count };
  update();
}
function updatePage(page: number) {
  filterData.value = { ...filterData.value, page };
  update();
}

function resetPage() {
  filterData.value = { ...filterData.value, page: 1 };
}
function updateSortBy(sorting: { key: string; order: 'asc' | 'desc' }[]) {
  if (sorting.length) {
    const { key, order } = sorting[0];

    filterData.value = { ...filterData.value, sort: key, direction: order };
    resetPage();
    update();
  }
}
</script>

<template>
  <v-container>
    <h2>Goals</h2>
    <ListFilter dense :options="[]" @filter="updateFilter" />
    <v-data-table-server
      density="compact"
      @click:row="editGoal"
      :headers="headers"
      :items="filteredItems"
      hover
      :itemsLength="total"
      :itemsPerPage="filterData.count"
      :page="filterData.page"
      @update:itemsPerPage="updateItemsPerPage"
      @update:page="updatePage"
      @update:sortBy="updateSortBy"
    >
      <template v-slot:item.creationDate="{ value }">
        {{ format(value, 'do MMMM yyyy') }}
      </template>
      <template v-slot:item.translated="{ item }">
        <div style="display: flex; flex-direction: row">
          <translated-state :item="item" />
          &nbsp;
          <v-tooltip
            :text="
              item.published
                ? 'This item is published'
                : 'This item is not published'
            "
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                :icon="item.published ? 'mdi-eye-check' : 'mdi-eye-outline'"
                :color="item.published ? 'green' : 'red'"
              ></v-icon>
            </template>
          </v-tooltip>
        </div>
      </template>
    </v-data-table-server>

    <v-btn @click="addGoal" color="success">Add Goal</v-btn>&nbsp;
  </v-container>
</template>
